import React, { useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { api } from '../api/client'
import { usePreferences } from '../context/usePreferences'
import PageShell from '../components/ui/PageShell'
import { BrandMark } from '../components/StudioAura'
import ProgressBar from '../components/ProgressBar'
import { SecondaryButton } from '../components/ui/Button'

const ALLOWED = ['.wav', '.mp3', '.flac']
const MAX_FILES = 500
const MAX_BATCH_GB = 40

function allowedFile(file) {
  const name = (file.name || '').toLowerCase()
  return ALLOWED.some((ext) => name.endsWith(ext))
}

function formatSize(bytes) {
  if (bytes == null || bytes === 0) return '–'
  const mb = bytes / (1024 * 1024)
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(bytes / 1024).toFixed(0)} KB`
}

const PROFILES = [
  { value: 'spotify_safe', label: 'Spotify Safe (-14 / -1.0)' },
  { value: 'spotify_optimized', label: 'Spotify Optimized (-10 / -1.0)' },
  { value: 'radio', label: 'Radio (-11 / -1.2)' },
  { value: 'club_modern', label: 'Club Modern (-9 / -1.0)' },
  { value: 'club_punch', label: 'Club Punch (-8 / -0.6) ★' },
  { value: 'dj_set', label: 'DJ Set (-9.5 / -0.8)' },
  { value: 'archive', label: 'Archive (-16 / -1.0)' },
  { value: 'club', label: 'Club (Legacy)' },
  { value: 'tidal_hifi', label: 'Tidal HiFi (-14 / -1.0)' },
  { value: 'apple_music', label: 'Apple Music (-16 / -1.0)' },
  { value: 'youtube', label: 'YouTube (-14 / -1.0)' },
]

export default function Upload() {
  const prefs = usePreferences()
  const [drag, setDrag] = useState(false)
  const [files, setFiles] = useState([])
  const [uploading, setUploading] = useState(false)
  const [uploadStatus, setUploadStatus] = useState('')
  const [progress, setProgress] = useState(0)
  const [error, setError] = useState('')
  const [targetProfile, setTargetProfile] = useState('spotify_safe')
  const [aiCleanupEnabled, setAiCleanupEnabled] = useState(true)
  const [aiCleanupStrength, setAiCleanupStrength] = useState('medium')
  const [thoroughMastering, setThoroughMastering] = useState(false)
  const [normalizationEnabled, setNormalizationEnabled] = useState(true)
  const navigate = useNavigate()
  const fileInputRef = useRef(null)

  const onDrop = useCallback((e) => {
    e.preventDefault()
    setDrag(false)
    const list = Array.from(e.dataTransfer.files).filter(allowedFile)
    setFiles((prev) => {
      const next = [...prev, ...list]
      if (next.length > MAX_FILES) {
        setError(`Maximal ${MAX_FILES} Dateien (insgesamt max. ${MAX_BATCH_GB} GB). Es werden nur die ersten ${MAX_FILES} übernommen.`)
        return next.slice(0, MAX_FILES)
      }
      setError('')
      return next
    })
  }, [])

  const onDragOver = useCallback((e) => {
    e.preventDefault()
    setDrag(true)
  }, [])

  const onDragLeave = useCallback((e) => {
    e.preventDefault()
    setDrag(false)
  }, [])

  const onSelect = (e) => {
    const list = Array.from(e.target.files || []).filter(allowedFile)
    setFiles((prev) => {
      const next = [...prev, ...list]
      if (next.length > MAX_FILES) {
        setError(`Maximal ${MAX_FILES} Dateien (insgesamt max. ${MAX_BATCH_GB} GB). Es werden nur die ersten ${MAX_FILES} übernommen.`)
        return next.slice(0, MAX_FILES)
      }
      setError('')
      return next
    })
    if (e.target) e.target.value = ''
  }

  const remove = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i))

  const submit = async () => {
    if (files.length === 0) {
      setError('Bitte mindestens eine Datei auswählen (WAV, MP3, FLAC).')
      return
    }
    setUploading(true)
    setError('')
    setUploadStatus('uploading')
    setProgress(0)
    const form = new FormData()
    files.forEach((f) => form.append('files', f))
    form.append('target_profile', targetProfile)
    form.append('ai_cleanup_enabled', aiCleanupEnabled ? '1' : '0')
    form.append('ai_cleanup_strength', aiCleanupStrength)
    form.append('thorough_mastering', thoroughMastering ? '1' : '0')
    form.append('normalization_enabled', normalizationEnabled ? '1' : '0')
    if (prefs.language) form.append('language', prefs.language)
    let didRedirect = false
    try {
      const result = await api.uploadWithProgress('/upload', form, (percent) => {
        setProgress(Math.round((percent / 100) * 90))
      })
      const data = result?.data ?? result ?? {}
      setUploadStatus('mastering')
      setProgress(100)
      const tracks = Array.isArray(data?.tracks) ? data.tracks : []
      const firstTrackId = tracks[0]?.id ?? data?.track_id ?? data?.id ?? null
      const jobCreated =
        result != null &&
        ((typeof data?.uploaded === 'number' && data.uploaded > 0) ||
          tracks.length > 0 ||
          data?.track_id != null ||
          data?.job_id != null ||
          data?.accepted === true)
      if (jobCreated) {
        setFiles([])
        setError('')
        didRedirect = true
        navigate(firstTrackId != null ? `/dashboard?track=${firstTrackId}` : '/dashboard', {
          replace: true,
          state: { highlightTrackId: firstTrackId ?? undefined },
        })
        return
      }
    } catch (err) {
      const msg = err?.message || ''
      if (msg.includes('credits') || msg.includes('Credits')) {
        setError(msg + ' Siehe Dashboard für Kontostand.')
      } else if (msg === 'Upload fehlgeschlagen' || msg === 'Request aborted' || /network|timeout|failed/i.test(msg)) {
        setError('Upload fehlgeschlagen. Bei großer Datei oder langsamer Verbindung: Bitte Dashboard prüfen – der Upload kann trotzdem durchgelaufen sein.')
      } else {
        setError(msg || 'Upload fehlgeschlagen')
      }
    } finally {
      if (!didRedirect) {
        setUploading(false)
        setProgress(0)
        setUploadStatus('')
      }
    }
  }

  return (
    <PageShell>
      <div className="mx-auto max-w-2xl space-y-6">
        <div>
          <div className="mb-1 flex items-center gap-2 text-[#00d4aa]">
            <BrandMark size={22} />
            <span className="text-sm font-medium text-white/70">Upload</span>
          </div>
          <h1 className="text-2xl font-semibold tracking-tight text-white">Track masterisieren</h1>
          <p className="mt-1 text-sm text-white/50">WAV, MP3 oder FLAC – Analyse und Mastering nach Streaming-Standards.</p>
        </div>

        <div
          className={`rounded-2xl border bg-[#12121a] transition-colors ${
            drag ? 'border-[#00d4aa]/60 bg-[#00d4aa]/5' : 'border-white/[0.08]'
          }`}
        >
          {uploading ? (
            <div className="px-6 py-12">
              <p className="text-center text-base text-white/80">
                {uploadStatus === 'uploading' && 'Datei wird übertragen…'}
                {uploadStatus === 'mastering' && 'Weiterleitung zum Dashboard…'}
              </p>
              <div className="mx-auto mt-5 max-w-sm">
                <div className="mb-2 flex justify-between text-sm text-white/45">
                  <span>Fortschritt</span>
                  <span className="tabular-nums text-[#00d4aa]">{progress}%</span>
                </div>
                <ProgressBar progress={progress} showLabel={false} />
              </div>
            </div>
          ) : (
            <>
              <input
                id="upload-file-input"
                ref={fileInputRef}
                type="file"
                multiple
                accept=".wav,.mp3,.flac,audio/wav,audio/mpeg,audio/flac"
                onChange={onSelect}
                className="sr-only"
                aria-hidden
                tabIndex={-1}
              />
              <label
                htmlFor="upload-file-input"
                className="flex cursor-pointer flex-col items-center justify-center px-6 py-14 text-center select-none"
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); onDrop(e) }}
                onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); onDragOver(e) }}
                onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); onDragLeave(e) }}
              >
                <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl border border-[#00d4aa]/30 bg-[#00d4aa]/10 text-2xl text-[#00d4aa]">
                  ↑
                </span>
                <p className="text-base font-medium text-white/90">Audio-Datei auswählen oder ablegen</p>
                <p className="mt-2 text-sm text-white/40">Klicken oder per Drag &amp; Drop · max. {MAX_FILES} Dateien / {MAX_BATCH_GB} GB</p>
              </label>
            </>
          )}
        </div>

        {!uploading && files.length > 0 && (
          <div className="space-y-4 rounded-2xl border border-white/[0.08] bg-[#12121a] p-6">
            <div className="space-y-2">
              {files.map((f, i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl border border-white/[0.06] bg-[#0a0a0f] p-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#00d4aa]/10 text-sm text-[#00d4aa]">♪</div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-white">{f.name}</p>
                    <p className="text-xs text-white/40">{formatSize(f.size)}</p>
                  </div>
                  <button type="button" onClick={() => remove(i)} className="text-xs text-white/45 hover:text-red-300">Entfernen</button>
                </div>
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-white/80">Mastering-Profil</label>
              <select
                value={targetProfile}
                onChange={(e) => setTargetProfile(e.target.value)}
                className="mt-2 w-full rounded-xl border border-white/10 bg-[#0a0a0f] px-4 py-3 text-white focus:border-[#00d4aa]/50 focus:outline-none focus:ring-1 focus:ring-[#00d4aa]/30"
              >
                {PROFILES.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>

            <div className="space-y-3 rounded-xl border border-white/[0.06] bg-[#0a0a0f] p-4">
              <label className="flex cursor-pointer items-center justify-between gap-4">
                <span className="text-sm text-white/85">AI Cleanup</span>
                <input type="checkbox" checked={aiCleanupEnabled} onChange={(e) => setAiCleanupEnabled(e.target.checked)} className="rounded accent-[#00d4aa]" />
              </label>
              {aiCleanupEnabled && (
                <select
                  value={aiCleanupStrength}
                  onChange={(e) => setAiCleanupStrength(e.target.value)}
                  className="w-full rounded-lg border border-white/10 bg-[#12121a] px-3 py-2 text-sm text-white"
                >
                  <option value="low">Niedrig</option>
                  <option value="medium">Mittel</option>
                  <option value="high">Hoch</option>
                </select>
              )}
              <label className="flex cursor-pointer items-start gap-3">
                <input type="checkbox" checked={normalizationEnabled} onChange={(e) => setNormalizationEnabled(e.target.checked)} className="mt-1 rounded accent-[#00d4aa]" />
                <span className="text-sm text-white/75">Lautheits-Normalisierung</span>
              </label>
              <label className="flex cursor-pointer items-start gap-3">
                <input type="checkbox" checked={thoroughMastering} onChange={(e) => setThoroughMastering(e.target.checked)} className="mt-1 rounded accent-[#00d4aa]" />
                <span className="text-sm text-white/75">Gründlicher Durchlauf</span>
              </label>
            </div>

            {error && (
              <div className="space-y-2">
                <div className="rounded-xl bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</div>
                {error.includes('Dashboard prüfen') && (
                  <SecondaryButton className="w-full" onClick={() => navigate('/dashboard')}>Zum Dashboard</SecondaryButton>
                )}
              </div>
            )}

            <button
              type="button"
              onClick={submit}
              disabled={files.length === 0}
              className="w-full rounded-lg bg-[#00d4aa] py-3.5 text-base font-semibold text-[#0a0a0f] transition hover:bg-[#1ae0b8] disabled:opacity-40"
            >
              Mastering starten
            </button>
          </div>
        )}
      </div>
    </PageShell>
  )
}
